import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import InlineFeedback from '../InlineFeedback';
import { useFeedback } from '../../hooks/useFeedback';
import { TIER_LABELS } from '../../utils/tiers';
import { PriceSetting } from '../../types';
import { formatDateTimeUTC } from '../../utils/dateTime';
import { dollarsToCents, centsToDollars } from '../../utils/money';
import { inputClass, LoadingSpinner } from './shared';

const PRICE_TYPE_LABELS: Record<string, string> = {
  input_tokens_converted: 'Input tokens converted',
};

const UNITS_PATTERN = /^\d+$/;
// Costs are exact micro-cents, so the units must divide one million.
const UNITS_SCALE = 1_000_000;

interface RowProps {
  setting: PriceSetting;
  onSaved: () => Promise<void>;
}

// One tier price: editable price and units, saved as a new version.
const PriceRow: React.FC<RowProps> = ({ setting, onSaved }) => {
  const [priceText, setPriceText] = useState(centsToDollars(setting.price_cents));
  const [unitsText, setUnitsText] = useState(String(setting.units));
  const [isSaving, setIsSaving] = useState(false);
  const feedback = useFeedback();

  // A refetch after save brings the new row. Reset the draft to it.
  useEffect(() => {
    setPriceText(centsToDollars(setting.price_cents));
    setUnitsText(String(setting.units));
  }, [setting.price_cents, setting.units]);

  const priceCents = dollarsToCents(priceText);
  const units = UNITS_PATTERN.test(unitsText.trim()) ? Number(unitsText) : null;
  const priceError = priceCents === null ? 'Enter dollars with up to 2 decimals.' : null;
  const unitsError = units === null || units < 1 || UNITS_SCALE % units !== 0 ? 'Enter a whole number that divides 1,000,000, such as 1, 1,000, or 1,000,000.' : null;
  const isDirty = priceCents !== setting.price_cents || units !== setting.units;
  const canSave = isDirty && !priceError && !unitsError && !isSaving;

  const handleSave = async () => {
    if (priceCents === null || units === null) return;
    setIsSaving(true);
    try {
      await apiService.updatePricing({ type: setting.type, tier: setting.tier, price_cents: priceCents, units });
      await onSaved();
      feedback.showSuccess('Price saved.');
    } catch (error) {
      console.error('Failed to save price:', error);
      feedback.showError('Failed to save price. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPriceText(centsToDollars(setting.price_cents));
    setUnitsText(String(setting.units));
    feedback.clear();
  };

  const priceId = `price-${setting.type}-${setting.tier}`;
  const unitsId = `units-${setting.type}-${setting.tier}`;

  return (
    <div className="bg-card-bg border border-secondary rounded-lg p-4 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <h4 className="font-medium text-heading">{TIER_LABELS[setting.tier]}</h4>
        </div>
        <div>
          <label htmlFor={priceId} className="block text-sm text-secondary mb-1">Price in dollars</label>
          <input
            id={priceId}
            type="text"
            inputMode="decimal"
            className={`${inputClass} w-full`}
            value={priceText}
            onChange={(e) => setPriceText(e.target.value)}
            disabled={isSaving}
          />
          {priceError && <p className="mt-1 text-xs text-red-400">{priceError}</p>}
        </div>
        <div>
          <label htmlFor={unitsId} className="block text-sm text-secondary mb-1">Number of units</label>
          <input
            id={unitsId}
            type="text"
            inputMode="numeric"
            className={`${inputClass} w-full`}
            value={unitsText}
            onChange={(e) => setUnitsText(e.target.value)}
            disabled={isSaving}
          />
          {unitsError && <p className="mt-1 text-xs text-red-400">{unitsError}</p>}
        </div>
      </div>

      <p className="mt-3 text-sm text-secondary">
        Effective from {formatDateTimeUTC(setting.effective_from)} UTC. Set by {setting.created_by}.
      </p>

      <div className="mt-4 flex items-center gap-4">
        {isDirty && (
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-secondary text-body-color rounded hover:bg-widget-bg"
            disabled={isSaving}
          >
            Cancel
          </button>
        )}
        <button onClick={handleSave} className="btn-primary" disabled={!canSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
        <InlineFeedback feedback={feedback.feedback} />
      </div>
    </div>
  );
};

// Admin pricing page. Each save closes the current price version and opens a new one.
const PricingSection: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['admin', 'pricing'],
    queryFn: apiService.getPricing,
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pricing'] });

  return (
    <div>
      <h2 className="text-xl font-bold text-heading mb-4">Pricing</h2>
      <p className="text-sm text-secondary mb-6">
        The price applies to usage from the moment you save. Earlier usage keeps the price that was valid at that time.
      </p>

      {error ? (
        <InlineFeedback feedback={{ kind: 'error', message: 'Failed to load prices.' }} className="max-w-md" />
      ) : isLoading || !settings ? (
        <LoadingSpinner />
      ) : (
        <div className="space-y-4">
          {Object.entries(PRICE_TYPE_LABELS).map(([type, label]) => (
            <section key={type} className="space-y-4">
              <h3 className="text-lg font-medium text-heading">{label}</h3>
              {settings.filter((setting) => setting.type === type).map((setting) => (
                <PriceRow key={setting.tier} setting={setting} onSaved={refresh} />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default PricingSection;
